import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import {
  fetchRefundCalculator,
  refundClawback,
  type RefundablePurchase,
  type RefundClawbackResult,
  type RefundNextStep,
} from '@/lib/adminCredits';
import {
  DEFAULT_REFUND_PERCENT,
  MAX_REFUND_PERCENT,
  MIN_REFUND_PERCENT,
  formatMoney,
  parseMoneyToCents,
  quoteRefund,
  type RefundQuote,
} from '@/lib/refundCalculator';
import { canGrantCredits } from '@/lib/roles';
import { useMe } from '@/lib/useMe';

/** Same floor as the API (admin_credits.MIN_REASON_LENGTH). MANDATORY here,
 * unlike a grant's: this takes coins away from a customer. */
const MIN_REASON_LENGTH = 10;

const QUERY_KEY = (orgId: string) => ['admin-refund-calculator', orgId] as const;

const FIELD =
  'min-w-0 rounded-card border border-border bg-background px-3 py-2 text-body text-fg-primary';
const LABEL = 'flex flex-col gap-1 text-meta font-medium text-fg-secondary';

function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

function coins(n: number): string {
  return Number.isInteger(n) ? n.toLocaleString('en-US') : n.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

/** Why a purchase cannot be picked, or null when it can. */
function purchaseBlocked(p: RefundablePurchase): string | null {
  if (p.clawback) {
    return `already refunded here: ${coins(p.clawback.coins_deducted)} coins cut${
      p.clawback.percent ? ` at ${p.clawback.percent}%` : ''
    }${p.clawback.decided_by ? ` by ${p.clawback.decided_by}` : ''}`;
  }
  if (p.reversed_by_provider > 0) {
    return `refunded by the payment provider: ${coins(p.reversed_by_provider)} coins reversed automatically`;
  }
  return null;
}

/**
 * The money half, which this screen does NOT perform. The console's only money
 * action, "Refund order" on the Payments page, refunds a WHOLE order — there is
 * no partial-amount refund anywhere in our stack — so at less than 100% the
 * partial refund is issued in the Solidgate dashboard.
 */
function NextStep({ step }: { step: RefundNextStep }) {
  const amount = formatMoney(step.amount_cents, step.currency);
  const order = step.provider_order_id ? ` for order ${step.provider_order_id}` : '';
  if (step.kind === 'refund_order') {
    return (
      <span>
        Refund the money: {amount}
        {order} with <strong>Refund order</strong> on the{' '}
        <Link className="underline" to="/payments">
          Payments page
        </Link>{' '}
        (finance role) — it refunds the whole order. When the provider confirms the refund, the
        rest of the pack is reversed automatically, minus what was cut here.
      </span>
    );
  }
  return (
    <span>
      Refund the money: issue a partial refund of <strong>{amount}</strong>
      {order} in the Solidgate dashboard. The console&apos;s Refund order action refunds the whole
      order and cannot do a partial amount.
    </span>
  );
}

export function RefundCalculatorPage() {
  const { orgId } = useParams<{ orgId: string }>();
  const { data: me } = useMe();
  const queryClient = useQueryClient();
  const canCut = canGrantCredits(me?.role);

  const { data, isLoading, error } = useQuery({
    queryKey: QUERY_KEY(orgId ?? ''),
    queryFn: () => fetchRefundCalculator(orgId!),
    enabled: !!orgId,
  });

  const [purchaseId, setPurchaseId] = useState<string | null>(null);
  const [price, setPrice] = useState('');
  const [currency, setCurrency] = useState<string | null>(null);
  const [packCoins, setPackCoins] = useState('');
  const [balance, setBalance] = useState('');
  const [percent, setPercent] = useState(DEFAULT_REFUND_PERCENT);
  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [idempotencyKey, setIdempotencyKey] = useState(newIdempotencyKey);
  const [result, setResult] = useState<
    { ok: true; res: RefundClawbackResult } | { ok: false; message: string } | null
  >(null);

  function pick(p: RefundablePurchase) {
    setPurchaseId(p.ledger_row_id);
    setPackCoins(String(p.coins));
    setPrice(p.price_cents !== null ? (p.price_cents / 100).toFixed(2) : '');
    setCurrency(p.currency);
  }

  // Prefill each time a NEW answer arrives (first load, and the refetch after
  // a confirm): the live wallet, and the newest purchase that can still be
  // refunded. Everything stays editable. Adjusting state during render on a
  // changed input is React's documented alternative to an effect here.
  const [prefilledFrom, setPrefilledFrom] = useState<typeof data>(undefined);
  if (data && data !== prefilledFrom) {
    setPrefilledFrom(data);
    setBalance(String(data.balance.wallet));
    const first = data.purchases.find((p) => !purchaseBlocked(p));
    if (first) pick(first);
    else {
      setPurchaseId(null);
      setCurrency(null);
    }
  }

  function byHand() {
    setPurchaseId(null);
    setCurrency(null);
  }

  const priceCents = parseMoneyToCents(price);
  const balanceNumber = balance.trim() === '' ? NaN : Number(balance);
  const preview = useMemo<{ quote: RefundQuote } | { error: string } | null>(() => {
    if (priceCents === null || packCoins.trim() === '' || !Number.isFinite(balanceNumber)) return null;
    try {
      return {
        quote: quoteRefund({
          priceCents,
          coinsPurchased: Number(packCoins),
          balance: balanceNumber,
          percent,
        }),
      };
    } catch (err) {
      return { error: (err as Error).message };
    }
  }, [priceCents, packCoins, balanceNumber, percent]);

  const quote = preview && 'quote' in preview ? preview.quote : null;
  const reasonOk = reason.trim().length >= MIN_REASON_LENGTH;
  const formValid = quote !== null && reasonOk;
  const selected = data?.purchases.find((p) => p.ledger_row_id === purchaseId) ?? null;
  const previewStep: RefundNextStep | null = quote
    ? {
        kind: quote.percent === MAX_REFUND_PERCENT && selected?.provider_order_id ? 'refund_order' : 'provider_partial_refund',
        amount_cents: quote.refundCents,
        currency,
        provider_order_id: selected?.provider_order_id ?? null,
      }
    : null;
  const walletDiffers = data && Number.isFinite(balanceNumber) && balanceNumber !== data.balance.wallet;

  const mutation = useMutation({
    mutationFn: () =>
      refundClawback(
        orgId!,
        {
          price_cents: priceCents!,
          coins_purchased: Number(packCoins),
          percent,
          reason: reason.trim(),
          entered_balance: balanceNumber,
          ...(purchaseId ? { purchase_ledger_row_id: purchaseId } : {}),
          ...(currency ? { currency } : {}),
        },
        idempotencyKey
      ),
    onSuccess: (res) => {
      setResult({ ok: true, res });
      setReason('');
      setIdempotencyKey(newIdempotencyKey());
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY(orgId ?? '') });
    },
    onError: (err: Error) => setResult({ ok: false, message: err.message }),
    onSettled: () => setConfirming(false),
  });

  if (!orgId) return null;

  return (
    <div className="min-w-0 max-w-3xl [overflow-wrap:anywhere]">
      <h1 className="text-h2 font-semibold text-fg-primary">
        Refund calculator — {data?.organization_name ?? orgId}
      </h1>
      <p className="mt-2 text-body text-fg-secondary">
        Refund part of a purchase and take back the same share of its coins. Default {DEFAULT_REFUND_PERCENT}%, up to{' '}
        {MAX_REFUND_PERCENT}% in hard cases. Example: 1,000 coins for $10 with 800 left — a 50% refund
        returns $5 and leaves 300 coins.{' '}
        <Link className="underline" to={`/organizations/${orgId}/credits`}>
          Back to credits
        </Link>
      </p>

      {isLoading && <p className="mt-4 text-body text-fg-secondary">Loading…</p>}
      {error && <p className="mt-4 text-body text-destructive-soft-foreground">{(error as Error).message}</p>}

      {data && (
        <>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="rounded-card border border-border bg-surface-field p-4">
              <div className="text-meta text-fg-secondary">Coins left from purchases (wallet)</div>
              <div className="mt-1 text-h3 font-semibold text-fg-primary">{coins(data.balance.wallet)}</div>
              <div className="mt-1 text-micro text-fg-tertiary">What the cut is taken from.</div>
            </div>
            <div className="rounded-card border border-border bg-surface-field p-4">
              <div className="text-meta text-fg-secondary">Total the user can spend now</div>
              <div className="mt-1 text-h3 font-semibold text-fg-primary">
                {data.balance.unlimited ? 'unlimited' : coins(data.balance.remaining ?? 0)}
              </div>
              <div className="mt-1 text-micro text-fg-tertiary">Wallet plus this month&apos;s plan allowance.</div>
            </div>
          </div>

          <h2 className="mt-6 text-h3 font-semibold text-fg-primary">Purchase</h2>
          <div className="mt-2 flex flex-col gap-2" role="radiogroup" aria-label="Purchase">
            {data.purchases.length === 0 && (
              <p className="text-body text-fg-secondary">
                No credit-pack purchases on record — type the numbers below by hand.
              </p>
            )}
            {data.purchases.map((p) => {
              const blocked = purchaseBlocked(p);
              return (
                <label
                  key={p.ledger_row_id}
                  className={`flex items-start gap-2 rounded-card border border-border p-3 text-body ${
                    blocked ? 'text-fg-tertiary' : 'text-fg-primary'
                  }`}
                >
                  <input
                    type="radio"
                    name="purchase"
                    checked={purchaseId === p.ledger_row_id}
                    disabled={!!blocked}
                    onChange={() => pick(p)}
                    className="mt-1"
                  />
                  <span className="min-w-0">
                    {coins(p.coins)} coins
                    {p.price_cents !== null ? ` for ${formatMoney(p.price_cents, p.currency)}` : ''}
                    {p.date_created ? ` · ${new Date(p.date_created).toLocaleString()}` : ''}
                    {p.provider_order_id ? ` · order ${p.provider_order_id}` : ''}
                    {blocked && <span className="block text-meta">{blocked}</span>}
                  </span>
                </label>
              );
            })}
            <label className="flex items-center gap-2 rounded-card border border-border p-3 text-body text-fg-primary">
              <input type="radio" name="purchase" checked={purchaseId === null} onChange={byHand} />
              Type the numbers by hand (no purchase on record)
            </label>
          </div>

          <form
            className="mt-6 flex flex-col gap-3 rounded-card border border-border bg-surface-field p-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (formValid && canCut) setConfirming(true);
            }}
          >
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <label className={LABEL}>
                Purchase amount{currency ? ` (${currency})` : ''}
                <input inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} className={FIELD} />
              </label>
              <label className={LABEL}>
                Coins in the purchase
                <input
                  type="number"
                  min={1}
                  step={1}
                  value={packCoins}
                  onChange={(e) => setPackCoins(e.target.value)}
                  className={FIELD}
                />
              </label>
              <label className={LABEL}>
                Coins left now
                <input
                  type="number"
                  step="any"
                  value={balance}
                  onChange={(e) => setBalance(e.target.value)}
                  className={FIELD}
                />
              </label>
            </div>

            <label className={LABEL} htmlFor="refund-percent">
              Refund share: {percent}%
              <div className="flex items-center gap-3">
                <input
                  id="refund-percent"
                  type="range"
                  min={MIN_REFUND_PERCENT}
                  max={MAX_REFUND_PERCENT}
                  step={1}
                  value={percent}
                  onChange={(e) => setPercent(Number(e.target.value))}
                  className="min-w-0 flex-1"
                  aria-label="Refund share slider"
                />
                <input
                  type="number"
                  min={MIN_REFUND_PERCENT}
                  max={MAX_REFUND_PERCENT}
                  step={1}
                  value={percent}
                  onChange={(e) => setPercent(Number(e.target.value))}
                  className={`${FIELD} w-24`}
                  aria-label="Refund share percent"
                />
              </div>
            </label>

            {preview && 'error' in preview && (
              <p role="alert" className="text-meta text-destructive-soft-foreground">
                {preview.error}
              </p>
            )}

            {quote && (
              <div data-testid="refund-quote" className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div className="rounded-card border border-border bg-background p-3">
                  <div className="text-meta text-fg-secondary">Refund to the customer</div>
                  <div className="mt-1 text-h3 font-semibold text-fg-primary">{formatMoney(quote.refundCents, currency)}</div>
                </div>
                <div className="rounded-card border border-border bg-background p-3">
                  <div className="text-meta text-fg-secondary">Coins to take back</div>
                  <div className="mt-1 text-h3 font-semibold text-fg-primary">{coins(quote.coinsDeducted)}</div>
                </div>
                <div className="rounded-card border border-border bg-background p-3">
                  <div className="text-meta text-fg-secondary">Coins left after</div>
                  <div className="mt-1 text-h3 font-semibold text-fg-primary">{coins(quote.balanceAfter)}</div>
                </div>
              </div>
            )}

            {quote && quote.shortfall > 0 && (
              <p
                role="note"
                className="flex items-start gap-2 rounded-card border border-warning-border bg-warning-soft px-3 py-2 text-meta text-warning-soft-foreground"
              >
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                <span>
                  The user already spent more than they keep at {quote.percent}%: {coins(quote.coinsToCut)} coins should
                  go, only {coins(quote.coinsDeducted)} are left, so the balance stops at 0. The largest refund the
                  remaining coins cover is {quote.fairPercent}%.
                </span>
              </p>
            )}

            {walletDiffers && (
              <p className="text-micro text-fg-tertiary">
                You changed &quot;Coins left now&quot; from the live wallet ({coins(data.balance.wallet)}). On confirm the
                cut is recomputed against the live wallet; your number is recorded beside it.
              </p>
            )}

            {previewStep && (
              <p className="text-meta text-fg-secondary">
                <NextStep step={previewStep} />
              </p>
            )}

            <label className={LABEL}>
              Reason (required, at least {MIN_REASON_LENGTH} characters)
              <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} className={FIELD} />
            </label>

            {canCut ? (
              <button
                type="submit"
                disabled={!formValid || mutation.isPending}
                className="self-start rounded-card bg-primary px-4 py-2 text-body font-medium text-primary-foreground hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
              >
                Take back coins
              </button>
            ) : (
              <p className="text-meta text-fg-tertiary">Your role can use the calculator but cannot take coins back.</p>
            )}
          </form>
        </>
      )}

      {result && (
        <div
          role="status"
          className={`mt-4 flex items-start gap-2 rounded-card border px-3 py-2 text-body ${
            result.ok
              ? 'border-success-border bg-success-soft text-success-soft-foreground'
              : 'border-destructive-border bg-destructive-soft text-destructive-soft-foreground'
          }`}
        >
          {result.ok ? (
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          ) : (
            <XCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          )}
          {result.ok ? (
            <span>
              {result.res.replayed ? 'Already done (repeated request) — ' : 'Done — '}
              {result.res.quote ? `${coins(result.res.quote.coins_deducted)} coins taken back, ${coins(result.res.quote.balance_after)} left. ` : ''}
              {result.res.next_step && <NextStep step={result.res.next_step} />}
            </span>
          ) : (
            result.message
          )}
        </div>
      )}

      {confirming && quote && (
        <ConfirmDialog
          title="Take back coins?"
          description={`Take ${coins(quote.coinsDeducted)} coins from ${
            data?.organization_name ?? orgId
          } for a ${quote.percent}% refund of ${formatMoney(quote.refundCents, currency)}. Reason: "${reason.trim()}". This writes one ledger row and an audit row; the money itself is refunded separately.`}
          confirmLabel="Take back coins"
          busy={mutation.isPending}
          onCancel={() => setConfirming(false)}
          onConfirm={() => {
            if (!mutation.isPending) mutation.mutate();
          }}
        />
      )}
    </div>
  );
}
